import type { FC } from 'react';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, useTheme } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import { calculateDifferences } from 'src/components/dashboard/DashboardPage/components/ComparisonPill/helper';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import WidgetLegend from 'src/components/dashboard/DashboardPage/widgets/SpeedometerWidget/WidgetLegend';
import Gauge from 'src/components/followUp/common/BaseWidgets/Gauge';
import type { AggregateList } from 'src/components/followUp/common/types';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import useNpsScoreCalculation from './hooks/useNpsScoreCalculation';
import { extractNpsData } from './helper';
import { useExportOptions } from './hooks';

const NpsScoreGaugeWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const ariaId = useId();
  const { t } = useTranslation('followUp');

  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.NPS_SPEEDOMETER,
    config.id,
  );

  const { data, isFetching, errorMsg, refetch, comparisonData, queryFilters } =
    useWidgetQuery<AggregateList>(endpoint);
  const { colors } = useTheme();

  const { npsScores: currentData } = useNpsScoreCalculation({ data });
  const { npsScores: previousData } = useNpsScoreCalculation({
    data: comparisonData,
  });

  const rawCurrentData = useMemo(() => extractNpsData(data), [data]);
  const {
    promoters: rawPromoters,
    passives: rawPassives,
    detractors: rawDetractors,
  } = rawCurrentData;

  const exportOptions = useExportOptions({
    data: rawCurrentData,
    comparisonData: previousData,
    widgetName: config.name,
    queryFilters,
  });

  const comparisonDataValues = useMemo(() => {
    if (!currentData || !previousData) return undefined;
    return calculateDifferences(
      currentData as unknown as Record<string, number | string | undefined>,
      previousData as unknown as Record<string, number | string | undefined>,
    );
  }, [currentData, previousData]);

  return (
    <WidgetFrame
      ariaId={ariaId}
      loading={isFetching}
      refetch={refetch}
      error={errorMsg}
      exportOptions={exportOptions}
      header={<WidgetHeaderWithPill title={config.name} ariaId={ariaId} />}
      footer={
        currentData?.totalScore === undefined && (
          <NoResultsToShowAdjustFiltersWarning />
        )
      }
    >
      {currentData?.totalScore !== undefined && (
        <Stack
          direction="column"
          sx={{ width: '100%', height: '100%', justifyContent: 'space-around' }}
        >
          <Gauge
            score={currentData?.totalScore ?? 0}
            comparisonScore={
              comparisonDataValues?.totalScore.absoluteDifference
            }
          />
          <WidgetLegend
            labelValuePairs={[
              {
                label: t('npsGroup.promoter'),
                value: `${currentData?.promoters ?? 0}%`,
                comparisonValue:
                  comparisonDataValues?.promoters.absoluteDifference,
                colorOverride: colors.tertiary[700],
                key: 'Promoter',
                toolTip: `n=${rawPromoters ?? 0}`,
              },
              {
                label: t('npsGroup.passive'),
                value: `${currentData?.passives ?? 0}%`,
                comparisonValue:
                  comparisonDataValues?.passives.absoluteDifference,
                colorOverride: colors.neutral[500],
                key: 'Passive',
                toolTip: `n=${rawPassives ?? 0}`,
              },
              {
                label: t('npsGroup.detractor'),
                value: `${currentData?.detractors ?? 0}%`,
                comparisonValue:
                  comparisonDataValues?.detractors.absoluteDifference,
                colorOverride: colors.error[600],
                key: 'Detractor',
                toolTip: `n=${rawDetractors ?? 0}`,
              },
            ]}
          />
        </Stack>
      )}
    </WidgetFrame>
  );
};
export default NpsScoreGaugeWidget;
